'use client';

import { cn } from '@savvy-kit/lib/utils';
import { isNil } from 'es-toolkit';
import { Zap } from 'lucide-react';

type CategoryIndicatorProps = {
  readonly hexColorCode?: string | null;
  readonly emoji?: string | null;
  readonly className?: string;
  /** When true, renders a placeholder swatch if both indicators are absent. */
  readonly placeholder?: boolean;
  /**
   * When true, renders a Zap glyph (smart-tag indicator) tinted with the
   * category color when present. Wins over emoji and color so smart tags
   * read consistently on every surface that uses this component. Mirrors
   * the kit `TagIndicator` smart branch.
   */
  readonly smart?: boolean;
};

/**
 * Renders a small visual indicator for a tag category — either an emoji glyph
 * or a colored swatch. Emoji wins when both are provided. When `smart` is set,
 * a Zap icon supersedes both.
 */
export function CategoryIndicator({
  hexColorCode,
  emoji,
  className,
  placeholder = false,
  smart = false,
}: CategoryIndicatorProps) {
  if (smart) {
    const hasHex = !isNil(hexColorCode) && hexColorCode !== '';
    return (
      <span
        role="img"
        aria-label="Smart tag"
        className={cn('inline-flex size-4 shrink-0 items-center justify-center', className)}
      >
        <Zap className="size-4" style={hasHex ? { color: hexColorCode, fill: hexColorCode } : undefined} aria-hidden />
      </span>
    );
  }

  if (!isNil(emoji) && emoji !== '') {
    return (
      <span
        aria-hidden="true"
        className={cn('inline-flex size-4 shrink-0 items-center justify-center text-base leading-none', className)}
      >
        {emoji}
      </span>
    );
  }

  if (!isNil(hexColorCode) && hexColorCode !== '') {
    return (
      <span
        aria-hidden="true"
        className={cn('inline-block size-4 shrink-0 rounded-full border border-light', className)}
        style={{ backgroundColor: hexColorCode }}
      />
    );
  }

  if (placeholder) {
    return (
      <span
        aria-hidden="true"
        className={cn('inline-block size-4 shrink-0 rounded-full border border-light bg-slate-300', className)}
      />
    );
  }

  return null;
}
